import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

// Context
import AuthContext from '../../authContext'

// UI
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import {
  IconButton,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
  Select,
  Checkbox
} from '@material-ui/core'

// Components
import Can from '../../components/Can'

// Rules
import {
  rulesAdminChangeOrderStatus,
  rulesOrderStatus
} from '../../utils/rulesOrderStatus'

import { translate } from '../../utils/helpers'
import jMoment from 'moment-jalaali'

// Type
import { Order } from './interfaces'
import showOrderTplType from '../../utils/temporaryConditions/showOrderTplType'
import showDeliveryType from '../../utils/temporaryConditions/showDeliveryType'
import RonadIcons from '../../components/iconComponent'
import SimpleTable, {
  TbodyProps,
  TheadProps
} from '../../components/Table/simpleTable'
import SimpleTableHead from '../../components/Table/tableHead'
import SimpleTableBody from '../../components/Table/tableBody'

type Data = Order
type Props = {
  data: any
  handleConfirmCanceleBox: (id: number) => void
  changeStatusOrderWhitId: any
  handleOpenConfirmUpdateState: any
  handlerSelectedOrdersId: (ordersId: number[]) => void
}

interface HeadCell {
  id: keyof Data
  label: string | JSX.Element

  format?: (row: any) => JSX.Element
}
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute'
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px'
    },
    renderValue: {
      paddingLeft: '32px'
    },
    radioGroup: {
      margin: '-8px 0'
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px'
      }
    },
    badge: {
      backgroundColor: '#fff5e9',
      borderRadius: '30px',
      color: '#ec6839',
      fontSize: '12px',
      border: '1px solid #ec6839',
      padding: '6px 12px'
    }
  })
)

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext)
  const classes = useStyles()
  let headCells: HeadCell[] = []

  const [selectedOrders, setSelectedOrders] = useState<number[]>([])
  const { handleConfirmCanceleBox: handleClickOpen, handlerSelectedOrdersId } =
    props

  const excludedStatuses = [
    'DELIVERING_TO_POST',
    'BUNDLED',
    'REQUESTED_TO_RETURN',
    'LOADED',
    'DELIVERED_TO_POST',
    'RETURNED',
    'DELIVERED',
    'CANCELLED',
    'DELIVERED'
  ]

  const handleCheckboxChange = (order: any) => {
    const { id: orderId, orderType } = order
    if (orderType === 'FROM_CENTER') {
      setSelectedOrders((prevSelected) =>
        prevSelected.includes(orderId)
          ? prevSelected.filter((id) => id !== orderId)
          : [...prevSelected, orderId]
      )
    }
  }

  const handlerToggleAll = () => {
    const allSelected = props.data
      .filter((order: any) => order.orderType === 'FROM_CENTER')
      .map((order: any) => order.id)

    if (selectedOrders.length === allSelected.length) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(allSelected)
    }
  }

  const isIndeterminate = () => {
    const totalSelectable = props.data.filter(
      (order: any) => order.orderType === 'FROM_CENTER'
    ).length
    return selectedOrders.length > 0 && selectedOrders.length < totalSelectable
  }

  const isAllSelected = () => {
    const totalSelectable = props.data.filter(
      (order: any) => order.orderType === 'FROM_CENTER'
    ).length
    return selectedOrders.length === totalSelectable
  }

  useEffect(() => {
    handlerSelectedOrdersId(selectedOrders)
  }, [selectedOrders])

  const action = (row: any) => (
    <>
      <div>
        <Link to={`/orders/${row.id}`}>
          <RonadIcons
            className='mr-2 text-green'
            name='documentOutline'
            title='جزئیات سفارش'
          />
        </Link>
        {(row.status === 'ORDERED' || row.status === 'SUPPLYING') &&
          row.orderType !== 'FROM_CENTER' && (
            <Can
              role={authContext.user.role}
              perform='orders:edit'
              yes={() => (
                <Link to={`/edit-order/${row.id}`} id='editOrderIcon'>
                  <RonadIcons
                    className='mr-2 pointer'
                    name='pencil'
                    title='ویرایش سفارش'
                  />
                </Link>
              )}
            />
          )}
        {row.orderType === 'FROM_CENTER' && (
          <Can
            role={authContext.user.role}
            perform='orders:edit'
            yes={() => (
              <Link to={`/crossdock/${row.id}`} id='editOrderIcon'>
                <RonadIcons
                  className='mr-2 pointer'
                  name='pencil'
                  title='ویرایش سفارش'
                />
              </Link>
            )}
          />
        )}
        <Can
          role={authContext.user.role}
          perform='orders:byBundle'
          yes={() =>
            row.status !== 'ORDERED' && row.status ? (
              <Link to={`/bundles/${row.bundleId}`}>
                <IconButton
                  aria-label='bundle'
                  size='small'
                  title='نمایش سفارش در بسته '
                  color='secondary'
                  data-cy='bundle'
                >
                  <RonadIcons
                    name='packageVariantClosed'
                    className='text-primary'
                  />
                </IconButton>
              </Link>
            ) : (
              <IconButton
                aria-label='bundle'
                size='small'
                title='بسته‌بندی نشده'
                disabled
                color='default'
                style={{ pointerEvents: 'fill' }}
                data-cy='bundle'
              >
                <RonadIcons name='packageVariantClosed' />
              </IconButton>
            )
          }
        />
        {row.status === 'RETURNED' && (
          <Link to={`/orders-resend/${row.id}`}>
            <RonadIcons name='boxArrowDown' className='text-primary' size={1} />
          </Link>
        )}
        <Can
          role={authContext.user.role}
          perform='orders:cancelOrders'
          yes={() =>
            !excludedStatuses.includes(row.status) && (
              <RonadIcons
                size={0.8}
                name='times'
                title={row.status === 'CANCELLED' ? 'لغو شده' : 'لغو سفارش'}
                onClick={() => handleClickOpen(row.id)}
                className={
                  row.status === 'CANCELLED'
                    ? 'text-gray ml-2'
                    : 'text-danger pointer ml-2'
                }
              />
            )
          }
        />
      </div>
    </>
  )

  Can({
    role: authContext.user.role,
    perform: 'orders:list:centerName',
    yes: () => {
      headCells = [
        {
          id: 'customerName',
          label: 'نام سفارش‌دهنده'
        },
        {
          id: 'id',
          label: 'کد سفارش',
          format: (row: any) => (
            <>
              {row.id}
              {row.warehouseType == 'MULTI' && (
                <span style={{ padding: '5px' }}>
                  <RonadIcons
                    name='plusMultiple'
                    className='text-green'
                    size={1}
                  />
                </span>
              )}
            </>
          )
        },
        { id: 'trackingCode', label: 'کد پیگیری' },
        {
          id: 'deliveryType',
          label: 'نوع ارسال',
          format: (row: any) => (
            <>
              {showDeliveryType(row.deliveryType)}
              &nbsp;
              {row.deliveryLocationType === 'IN_RONAD' && (
                <span>(تحویل درب انبار)</span>
              )}
            </>
          )
        },
        { id: 'centerName', label: 'مرکز سفارش' },
        {
          id: 'deliveryDate',
          label: 'زمان تحویل',
          format: (row) => (
            <>
              <span>
                {jMoment(row.deliveryDate, 'YYYY/M/D')
                  .locale('fa')
                  .format('jYY/jMM/jDD')}
              </span>
              &nbsp; &nbsp;- &nbsp; &nbsp;
              <span>
                {`${jMoment(row.deliveryStartTime, 'HH:mm:ss')
                  .locale('fa')
                  .format('HH')}
            `}
                {`تا ${jMoment(row.deliveryEndTime, 'HH:mm:ss')
                  .locale('fa')
                  .format('HH')}`}
              </span>
            </>
          )
        },
        {
          id: 'status',
          label: 'وضعیت سفارش',
          format: (row) => (
            <>
              <FormControl style={{ width: '100%' }}>
                <RonadIcons name='pencil' className={classes.changStatusIcon} />
                <Select
                  className={classes.changStatusBox}
                  fullWidth
                  name={`status${index}`}
                  multiple
                  id={row.id}
                  value={[{}]}
                  IconComponent={() => null}
                  renderValue={() => (
                    <span className={classes.renderValue} data-cy='status'>
                      {translate(row.status, 'order')}
                    </span>
                  )}
                >
                  <RadioGroup
                    className={classes.radioGroup}
                    aria-label={`status${index}`}
                    name={`status${index}`}
                    value={'item'}
                    onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
                      props.handleOpenConfirmUpdateState({
                        id: row.id,
                        body: `${e.target.value}`
                      })
                    }
                  >
                    {rulesOrderStatus[row.status]?.map(
                      (item: any, index: number) => (
                        <FormControlLabel
                          className={classes.radioGroupItem}
                          key={index}
                          checked={row.status === item}
                          value={item}
                          disabled={
                            rulesAdminChangeOrderStatus[
                              authContext.user.role
                            ].indexOf(item) === -1
                          }
                          control={<Radio />}
                          label={translate(item, 'order')}
                        />
                      )
                    )}
                  </RadioGroup>
                </Select>
              </FormControl>
            </>
          )
        },
        {
          id: 'codValue',
          label: 'شیوه پرداخت',
          format: (row) => <>{translate(row.paymentType, 'cod')}</>
        }
      ]
    },

    no: () => {
      headCells = [
        {
          id: 'checkbox',
          label: (
            <Checkbox
              indeterminate={isIndeterminate()}
              onChange={handlerToggleAll}
              checked={isAllSelected()}
              color='primary'
              size='small'
            />
          ),
          format: (row: any) => (
            <Checkbox
              onChange={() => handleCheckboxChange(row)}
              checked={selectedOrders.includes(row.id)}
              disabled={row.orderType !== 'FROM_CENTER'}
              color='primary'
              size='small'
            />
          )
        },
        {
          id: 'customerName',
          label: 'نام گیرنده'
        },
        {
          id: 'id',
          label: 'شناسه سفارش',
          format: (row: any) => (
            <>
              {row.id}
              {row.warehouseType == 'MULTI' && (
                <span style={{ padding: '5px' }}>
                  <RonadIcons
                    name='plusMultiple'
                    className='text-green'
                    size={1}
                  />
                </span>
              )}
            </>
          )
        },
        { id: 'trackingCode', label: 'کد پیگیری' },
        {
          id: 'deliveryType',
          label: ' نوع سفارش',
          format: (row: any) => (
            <>
              {row.fulfillmentTypeName ? (
                row.fulfillmentTypeName
              ) : (
                <>
                  {showDeliveryType(row.deliveryType)}
                  &nbsp;
                  {row.deliveryLocationType === 'IN_RONAD' && (
                    <span>(تحویل درب انبار)</span>
                  )}
                </>
              )}
            </>
          )
        },
        {
          id: 'orderTPLType',
          label: 'ناوگان حمل',
          format: (row: any) => (
            <>
              {row.courierName
                ? row.courierName
                : showOrderTplType(row.deliveryType, row.orderTPLType)}
            </>
          )
        },
        {
          id: 'deliveryDate',
          label: 'زمان تحویل',
          format: (row) => (
            <>
              <span>
                {jMoment(row.deliveryDate, 'YYYY/M/D')
                  .locale('fa')
                  .format('jYY/jMM/jDD')}
              </span>
              &nbsp; &nbsp;- &nbsp; &nbsp;
              <span>
                {`${jMoment(row.deliveryStartTime, 'HH:mm:ss')
                  .locale('fa')
                  .format('HH')}
            `}
                {`تا ${jMoment(row.deliveryEndTime, 'HH:mm:ss')
                  .locale('fa')
                  .format('HH')}`}
              </span>
            </>
          )
        },
        {
          id: 'status',
          label: 'وضعیت سفارش',
          format: (row) => (
            <>
              <span className={row.status === 'SUPPLYING' ? classes.badge : ''}>
                {translate(row.status, 'order')}
              </span>
            </>
          )
        },
        {
          id: 'codValue',
          label: 'شیوه پرداخت',
          format: (row) => <>{translate(row.paymentType, 'cod')}</>
        },
        {
          id: 'origin',
          label: 'مبدا ایجاد',
          format: (row) => translate(row.origin)
        }
      ]
    }
  })

  return (
    <SimpleTable
      Thead={(prop: TheadProps) => (
        <SimpleTableHead headCells={headCells} action={action} {...prop} />
      )}
      Tbody={(prop: TbodyProps) => (
        <SimpleTableBody
          celles={headCells}
          data={props.data}
          action={action}
          {...prop}
        />
      )}
    />
  )
}
